import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('admin_token');

export default function ImageUpload({ value, onChange, label = 'Image' }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Max 5MB'); return; }

    setUploading(true); setError('');
    const formData = new FormData();
    formData.append('image', file);

    try {
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div data-testid="image-upload">
      <label className="text-sm font-medium">{label}</label>
      <div className="mt-1.5 space-y-2">
        {/* Preview */}
        {value && (
          <div className="relative inline-block">
            <img src={value} alt="Preview" className="h-24 w-auto rounded-xl border border-border object-cover" />
            <button onClick={() => onChange('')} className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center hover:bg-destructive/80" data-testid="remove-image">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Upload + URL input */}
        <div className="flex gap-2">
          <Input
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="Image URL or upload..."
            className="rounded-xl text-sm flex-1"
            data-testid="image-url-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-xl px-3 flex-shrink-0"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="upload-image-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          </Button>
        </div>

        <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    </div>
  );
}
